/**
 * Общая палитра тёмной и светлой темы для всех страниц DeepRoller.
 * Используется совместно с useAppTheme() hook.
 */

export const DARK = {
  bg: '#0f172a',
  paper: 'rgba(15,23,42,0.88)',
  paperBorder: 'rgba(148,163,184,0.1)',
  paperSolid: '#1e293b',
  text: '#f1f5f9',
  textSec: '#cbd5e1',
  divider: 'rgba(148,163,184,0.12)',
  hover: 'rgba(99,102,241,0.08)',
  selected: 'rgba(99,102,241,0.15)',
  accent: '#818cf8',
  input: {
    color: '#f8fafc',
    border: 'rgba(148,163,184,0.34)',
    borderHover: 'rgba(148,163,184,0.5)',
    placeholder: '#e2e8f0',
    bg: 'rgba(15,23,42,0.95)',
  },
  chip: {
    border: 'rgba(148,163,184,0.2)',
    color: '#94a3b8',
  },
  tableHeader: '#1e293b',
  tableRowAlt: 'rgba(148,163,184,0.04)',
  tableRowHover: 'rgba(99,102,241,0.06)',
}

export const LIGHT = {
  bg: 'transparent',
  paper: 'rgba(255,255,255,0.92)',
  paperBorder: 'rgba(0,0,0,0.08)',
  paperSolid: '#fff',
  text: '#0f172a',
  textSec: '#475569',
  divider: 'rgba(0,0,0,0.08)',
  hover: 'rgba(25,118,210,0.04)',
  selected: 'rgba(25,118,210,0.08)',
  accent: '#1976d2',
  input: {
    color: '#0f172a',
    border: 'rgba(15,23,42,0.22)',
    borderHover: 'rgba(15,23,42,0.4)',
    placeholder: '#64748b',
    bg: '#ffffff',
  },
  chip: {},
  tableHeader: 'grey.50',
  tableRowAlt: 'rgba(0,0,0,0.015)',
  tableRowHover: 'rgba(25,118,210,0.06)',
}

export const getTheme = (dark) => dark ? DARK : LIGHT

/**
 * Общие стили для тёмных TextField
 */
export const darkInputSx = (T) => ({
  '& .MuiInputBase-input::placeholder, & .MuiInputBase-inputMultiline::placeholder': {
    color: T.input.placeholder,
    opacity: 1,
  },
  '& .MuiInputBase-input, & .MuiInputBase-inputMultiline': {
    color: T.input.color,
  },
  '& .MuiOutlinedInput-root': {
    color: T.input.color,
    background: T.input.bg,
    '& fieldset': { borderColor: T.input.border },
    '&:hover fieldset': { borderColor: T.input.borderHover },
    '&.Mui-focused fieldset': { borderColor: T.accent },
    '& input::placeholder, & textarea::placeholder': { color: T.input.placeholder, opacity: 1 },
    '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & textarea:-webkit-autofill, & textarea:-webkit-autofill:hover, & textarea:-webkit-autofill:focus': {
      WebkitTextFillColor: T.input.color,
      caretColor: T.input.color,
      WebkitBoxShadow: `0 0 0 1000px ${T.input.bg} inset`,
      boxShadow: `0 0 0 1000px ${T.input.bg} inset`,
      borderRadius: 'inherit',
      transition: 'background-color 9999s ease-in-out 0s',
    },
  },
  '& .MuiInputLabel-root': { color: T.textSec },
  '& .MuiInputLabel-root.Mui-focused': { color: T.accent },
  '& .MuiFormHelperText-root': { color: T.textSec },
  '& .MuiSvgIcon-root': { color: T.textSec },
  '& .MuiFormControlLabel-label': { color: T.text },
  '& .MuiTypography-root': { color: 'inherit' },
})

export const lightInputSx = (T) => ({
  '& .MuiInputBase-input::placeholder, & .MuiInputBase-inputMultiline::placeholder': {
    color: T.input.placeholder,
    opacity: 1,
  },
  '& .MuiInputBase-input, & .MuiInputBase-inputMultiline': {
    color: T.input.color,
  },
  '& .MuiOutlinedInput-root': {
    color: T.input.color,
    background: T.input.bg,
    '& fieldset': { borderColor: T.input.border },
    '&:hover fieldset': { borderColor: T.input.borderHover },
    '&.Mui-focused fieldset': { borderColor: T.accent },
  },
  '& .MuiInputLabel-root': { color: T.textSec },
  '& .MuiInputLabel-root.Mui-focused': { color: T.accent },
  '& .MuiFormHelperText-root': { color: T.textSec },
  '& .MuiSvgIcon-root': { color: T.textSec },
  '& .MuiFormControlLabel-label': { color: T.text },
})

/**
 * Стили фона страницы
 */
export const pageBgSx = (dark) => dark
  // Тёмная тема: подложка прозрачная — сквозь неё виден глобальный
  // анимированный фон «Liquid Silk» (см. common/AnimatedBackground.jsx).
  ? { background: 'transparent', minHeight: '100vh' }
  : {
      minHeight: '100vh',
      backgroundImage: 'url("/assets/promo-bg.jpg")',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
    }

/**
 * Старые дрейфующие blob'ы отключены: в тёмной теме их заменил глобальный
 * анимированный фон «Liquid Silk» (common/AnimatedBackground.jsx).
 * Массив оставлен пустым, чтобы постраничный рендер blob'ов ничего не рисовал.
 */
export const BLOBS = []
